/**
 * New builds (docs/architecture.md §13). The service worker looks for one when the game opens,
 * whenever it comes back on screen, and every 10 minutes while it's shown, so a tab or installed
 * app left open doesn't keep playing an old build against a newer server. A new build takes over
 * with a reload at the next calm moment: on Home, Profile or Settings with no sheet or popup
 * open (or on the Result screen while the game is in the background). Never during matchmaking,
 * a fight, or Training with picks not confirmed yet.
 */
import { useApp, type Screen } from '../state/store';
import { diag } from './diag';

const CHECK_EVERY_MS = 10 * 60_000;
/** Coming back on screen checks at most this often. */
const MIN_GAP_MS = 60_000;
/** Screens a reload can't hurt: nothing is going on and nothing is waiting to be saved. */
const CALM: readonly Screen[] = ['home', 'profile', 'settings'];

let registration: ServiceWorkerRegistration | undefined;
let lastCheck = 0;

/** Asks the server for a newer build (`now`: even if it asked a moment ago). */
export function checkForUpdate(now = false): void {
  if (!registration || !navigator.onLine) return;
  if (!now && Date.now() - lastCheck < MIN_GAP_MS) return;
  lastCheck = Date.now();
  diag.mark('update: checking for a new build');
  void registration.update().catch(() => undefined);
}

function calm(): boolean {
  const { screen, authSheet } = useApp.getState();
  // Any sheet or popup: the sign-in sheet waiting for a code, a confirm, a level-up.
  if (authSheet || document.querySelector('[role="dialog"]')) return false;
  return CALM.includes(screen) || (document.hidden && screen === 'result');
}

let reloading = false;

/** A new build took over: reload now, or as soon as it's calm. */
function reloadWhenCalm(): void {
  if (reloading) return;
  reloading = true;
  diag.mark('update: a new build is ready');
  const attempt = () => {
    if (calm()) location.reload();
  };
  attempt();
  setInterval(attempt, 1000);
  document.addEventListener('visibilitychange', attempt);
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  if (new URLSearchParams(location.search).has('nosw')) return;
  void import('virtual:pwa-register').then(({ registerSW }) =>
    registerSW({
      immediate: true,
      onNeedReload: reloadWhenCalm,
      onRegisteredSW: (_url: string, reg: ServiceWorkerRegistration | undefined) => {
        registration = reg;
        diag.mark(`update: watching for new builds (${reg ? 'registered' : 'no registration'})`);
        // Registering just checked.
        lastCheck = Date.now();
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) checkForUpdate();
        });
        setInterval(() => {
          if (!document.hidden) checkForUpdate(true);
        }, CHECK_EVERY_MS);
      },
      onRegisterError: (err: unknown) => diag.error(`service worker: ${String(err)}`),
    }),
  );
}
